import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './common';

export const TemplateLanguageSchema = z
  .string()
  .regex(/^[a-z]{2}(_[A-Z]{2})?$/, 'Formato esperado: pt_BR, en_US, etc.');
export type TemplateLanguage = z.infer<typeof TemplateLanguageSchema>;

export const TemplateStatusSchema = z.enum(['pending', 'approved', 'rejected', 'paused']);
export type TemplateStatus = z.infer<typeof TemplateStatusSchema>;

/**
 * Categoria Meta — afeta pricing dramaticamente:
 * - **marketing**: $0.0625/msg BR (campanhas, promo, broadcast).
 * - **utility**: ~$0.0080/msg BR (~8x mais barato — confirmações, OTPs,
 *   updates de pedido). Usado quando há ação iniciada pelo cliente.
 * - **authentication**: ~$0.0315/msg BR (códigos de verificação, login).
 *
 * Templates incorretamente categorizados são rejeitados pela Meta. UI
 * mostra preview do custo estimado pra ajudar escolha.
 */
export const TemplateCategorySchema = z.enum(['marketing', 'utility', 'authentication']);
export type TemplateCategory = z.infer<typeof TemplateCategorySchema>;

export const TemplateVariableSchema = z.object({
  name: z.string().min(1),
  position: z.number().int().positive(),
  example: z.string().optional(),
});
export type TemplateVariable = z.infer<typeof TemplateVariableSchema>;

export const TemplateSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  /**
   * Connection (WABA) onde o template é criado/sincronizado na Meta. `null`
   * em templates legados anteriores a esta coluna — caem no fallback de
   * "primeira connection ativa" no sync.
   */
  connectionId: UuidSchema.nullable(),
  /**
   * Label da connection dona do template (`connections.label`), resolvido via
   * JOIN no service. Ausente quando o template não tem `connectionId` vinculado
   * (template legado) ou quando a connection foi removida. Exibido na UI para
   * "reconhecer o dono" do template. O JOIN não filtra `archivedAt` — uma
   * connection arquivada ainda aparece como dono histórico.
   */
  connectionLabel: z.string().optional(),
  /**
   * WABA ID da connection dona (`connections.wabaId`), resolvido via mesmo
   * JOIN. Ausente nas mesmas condições que `connectionLabel`.
   */
  wabaId: z.string().optional(),
  metaTemplateName: z.string().min(1),
  language: TemplateLanguageSchema,
  category: TemplateCategorySchema,
  status: TemplateStatusSchema,
  variables: z.array(TemplateVariableSchema),
  body: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Template = z.infer<typeof TemplateSchema>;

export const CreateTemplateInputSchema = z.object({
  /** WABA destino — obrigatória: define em qual conta Meta o template é criado. */
  connectionId: UuidSchema,
  metaTemplateName: z.string().min(1),
  language: TemplateLanguageSchema,
  category: TemplateCategorySchema.default('marketing'),
  variables: z.array(TemplateVariableSchema),
  body: z.string().min(1),
});
export type CreateTemplateInput = z.infer<typeof CreateTemplateInputSchema>;

export const TemplatesListQuerySchema = z.object({
  /** Filtra por status — aplicado no SQL para que `total` e a paginação
   * reflitam o recorte. Usado pela tela de Templates para contar/sincronizar
   * os pendentes em escopo tenant-wide, não só a página visível. */
  status: TemplateStatusSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type TemplatesListQuery = z.infer<typeof TemplatesListQuerySchema>;

export const TemplatesListResponseSchema = z.object({
  templates: z.array(TemplateSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type TemplatesListResponse = z.infer<typeof TemplatesListResponseSchema>;
